/**
 * Sensor Management Section
 * Displays assigned sensors with calibration controls.
 * "Add Sensors" opens a dialog to pick from account or site sensors.
 */

import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, X, Cpu, Beaker, ClipboardList, Check, ChevronsUpDown, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useUpdateInstallationJob, useTemplates } from '../hooks/useInstallationJobs';
import { useCalibrationProcedures } from '../hooks/useCalibrationProcedures';
import { AddSensorsDialog } from './AddSensorsDialog';
import { AddAccountSensorsDialog } from './AddAccountSensorsDialog';
import type { InstallationJob, InstallationJobTemplate } from '../types';

interface ProcedureComboboxProps {
  value: string;
  procedures: { _id: string; name: string }[];
  disabled?: boolean;
  onSelect: (procedureId: string) => void;
}

function ProcedureCombobox({ value, procedures, disabled, onSelect }: ProcedureComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = procedures.find((p) => p._id === value);

  const filtered = useMemo(() => {
    if (!search) return procedures;
    const q = search.toLowerCase();
    return procedures.filter(
      (p) => p.name.toLowerCase().includes(q),
    );
  }, [procedures, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-7 w-[220px] justify-between text-xs font-normal',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="truncate">
            {selected ? selected.name : 'Select procedure...'}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search procedures..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No procedure found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((proc) => (
                <CommandItem
                  key={proc._id}
                  value={proc._id}
                  onSelect={() => {
                    onSelect(proc._id);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      value === proc._id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="text-xs font-medium">{proc.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface TemplateComboboxProps {
  value: string;
  templates: InstallationJobTemplate[];
  disabled?: boolean;
  onSelect: (templateId: string) => void;
}

function TemplateCombobox({ value, templates, disabled, onSelect }: TemplateComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = templates.find((t) => t.id === value);

  const filtered = useMemo(() => {
    if (!search) return templates;
    const q = search.toLowerCase();
    return templates.filter((t) => t.name.toLowerCase().includes(q));
  }, [templates, search]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-7 w-[220px] justify-between text-xs font-normal',
            !selected && 'text-muted-foreground',
          )}
        >
          <span className="truncate">
            {selected ? selected.name : 'Select checklist...'}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search checklists..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No checklist found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((tpl) => (
                <CommandItem
                  key={tpl.id}
                  value={tpl.id}
                  onSelect={() => {
                    onSelect(tpl.id);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      value === tpl.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="text-xs font-medium">{tpl.name}</span>
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({tpl.checklistItems.length})
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface SensorManagementSectionProps {
  job: InstallationJob;
}

export function SensorManagementSection({ job }: SensorManagementSectionProps) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [confirmRemoveTemplateId, setConfirmRemoveTemplateId] = useState<string | null>(null);
  const [pendingTemplateSwap, setPendingTemplateSwap] = useState<{ sensorId: string; templateId: string } | null>(null);
  const updateJob = useUpdateInstallationJob();
  const { data: procedures = [] } = useCalibrationProcedures();
  const { data: templatesResponse } = useTemplates();
  const templates = templatesResponse?.templates?.filter((t) => t.isActive) ?? [];

  const hasSite = !!(job.mongoSiteId || job.siteId);
  const hasAccount = !!job.accountId;
  const canAdd = hasAccount || hasSite;

  const handleToggleCalibration = async (mongoSensorId: string, checked: boolean) => {
    const updated = job.sensors.map((s) =>
      s.mongoSensorId === mongoSensorId
        ? {
            ...s,
            calibrationRequired: checked,
            ...(checked ? {} : { measurementProcedureId: undefined, measurementProcedureName: undefined }),
          }
        : s,
    );
    try {
      await updateJob.mutateAsync({ id: job.id, input: { sensors: updated } });
      toast.success(checked ? 'Calibration enabled' : 'Calibration disabled');
    } catch {
      toast.error('Failed to update calibration setting');
    }
  };

  const handleProcedureChange = async (mongoSensorId: string, procedureId: string) => {
    const procedure = procedures.find((p) => p._id === procedureId);
    if (!procedure) return;
    const updated = job.sensors.map((s) =>
      s.mongoSensorId === mongoSensorId
        ? { ...s, measurementProcedureId: procedure._id, measurementProcedureName: procedure.name }
        : s,
    );
    try {
      await updateJob.mutateAsync({ id: job.id, input: { sensors: updated } });
      toast.success('Measurement procedure assigned');
    } catch {
      toast.error('Failed to assign measurement procedure');
    }
  };

  const handleTemplateChange = (mongoSensorId: string, templateId: string) => {
    const sensor = job.sensors.find((s) => s.mongoSensorId === mongoSensorId);
    if (sensor?.checklist?.some((item) => item.completedAt)) {
      setPendingTemplateSwap({ sensorId: mongoSensorId, templateId });
      return;
    }
    doApplyTemplate(mongoSensorId, templateId);
  };

  const doApplyTemplate = async (mongoSensorId: string, templateId: string) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    const updated = job.sensors.map((s) =>
      s.mongoSensorId === mongoSensorId
        ? {
            ...s,
            checklistTemplateId: tpl.id,
            checklistTemplateName: tpl.name,
            checklist: tpl.checklistItems.map((item, index) => ({
              ...item,
              id: crypto.randomUUID(),
              sortOrder: item.sortOrder ?? index,
            })),
          }
        : s,
    );
    try {
      await updateJob.mutateAsync({ id: job.id, input: { sensors: updated } });
      toast.success(`Checklist "${tpl.name}" applied to sensor`);
    } catch {
      toast.error('Failed to apply checklist template');
    }
  };

  const handleRemoveTemplate = (mongoSensorId: string) => {
    const sensor = job.sensors.find((s) => s.mongoSensorId === mongoSensorId);
    const hasResponses = sensor?.checklist?.some((item) => item.completedAt);
    if (hasResponses) {
      setConfirmRemoveTemplateId(mongoSensorId);
    } else {
      doRemoveTemplate(mongoSensorId);
    }
  };

  const doRemoveTemplate = async (mongoSensorId: string) => {
    const updated = job.sensors.map((s) =>
      s.mongoSensorId === mongoSensorId
        ? { ...s, checklistTemplateId: undefined, checklistTemplateName: undefined, checklist: undefined }
        : s,
    );
    try {
      await updateJob.mutateAsync({ id: job.id, input: { sensors: updated } });
      toast.success('Sensor checklist removed');
    } catch {
      toast.error('Failed to remove checklist template');
    }
  };

  const handleRemove = async (mongoSensorId: string) => {
    const sensor = job.sensors.find((s) => s.mongoSensorId === mongoSensorId);
    if (sensor?.installed) {
      toast.error('Cannot remove an installed sensor');
      return;
    }
    const updated = job.sensors.filter((s) => s.mongoSensorId !== mongoSensorId);
    try {
      await updateJob.mutateAsync({
        id: job.id,
        input: { sensors: updated },
      });
      toast.success('Sensor removed');
    } catch {
      toast.error('Failed to remove sensor');
    }
  };

  const renderAssignedSensor = (sensor: InstallationJob['sensors'][number]) => (
    <div key={sensor.mongoSensorId} className="rounded border p-2 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-sm">{sensor.sensorName}</p>
          {sensor.flags.length > 0 && (
            <div className="flex gap-1 mt-1">
              {sensor.flags.map((flag) => (
                <Badge key={flag} variant="outline" className="text-xs">
                  {flag}
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {sensor.calibrationStatus && sensor.calibrationStatus !== 'pending' && (
            <Badge
              variant={
                sensor.calibrationStatus === 'completed' || sensor.calibrationStatus === 'applied'
                  ? 'success'
                  : 'info'
              }
            >
              {sensor.calibrationStatus === 'in_progress' ? 'Calibrating' : sensor.calibrationStatus}
            </Badge>
          )}
          <Badge variant={sensor.installed ? 'success' : 'secondary'}>
            {sensor.installed ? 'Installed' : 'Pending'}
          </Badge>
          {!sensor.installed && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={() => handleRemove(sensor.mongoSensorId)}
              disabled={updateJob.isPending}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Calibration toggle */}
      <div className="flex items-center gap-3 pl-1">
        <div className="flex items-center gap-2">
          <Beaker className="h-3.5 w-3.5 text-muted-foreground" />
          <Label htmlFor={`cal-${sensor.mongoSensorId}`} className="text-xs text-muted-foreground cursor-pointer">
            Calibration Required
          </Label>
          <Switch
            id={`cal-${sensor.mongoSensorId}`}
            checked={sensor.calibrationRequired ?? false}
            onCheckedChange={(checked) => handleToggleCalibration(sensor.mongoSensorId, checked)}
            disabled={updateJob.isPending}
          />
        </div>

        {sensor.calibrationRequired && (
          <ProcedureCombobox
            value={sensor.measurementProcedureId ?? ''}
            procedures={procedures}
            disabled={updateJob.isPending}
            onSelect={(procedureId) => handleProcedureChange(sensor.mongoSensorId, procedureId)}
          />
        )}
      </div>

      {/* Checklist template */}
      <div className="flex items-center gap-3 pl-1">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
          <Label className="text-xs text-muted-foreground">Checklist</Label>
        </div>
        {sensor.checklistTemplateId ? (
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-xs">
              {sensor.checklistTemplateName} ({sensor.checklist?.length ?? 0} items)
            </Badge>
            {!sensor.installed && (
              <>
                <TemplateCombobox
                  value={sensor.checklistTemplateId}
                  templates={templates}
                  disabled={updateJob.isPending}
                  onSelect={(templateId) => handleTemplateChange(sensor.mongoSensorId, templateId)}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemoveTemplate(sensor.mongoSensorId)}
                  disabled={updateJob.isPending}
                >
                  <X className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        ) : (
          <TemplateCombobox
            value=""
            templates={templates}
            disabled={updateJob.isPending || sensor.installed}
            onSelect={(templateId) => handleTemplateChange(sensor.mongoSensorId, templateId)}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2">
            <Cpu className="h-4 w-4" />
            Sensors
            {job.sensors.length > 0 && (
              <Badge variant="secondary" className="ml-1">{job.sensors.length}</Badge>
            )}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsAddDialogOpen(true)}
            disabled={!canAdd}
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Sensors
          </Button>
        </CardHeader>
        <CardContent>
          {!canAdd ? (
            <p className="text-sm text-muted-foreground">
              Select a site or account on this job to add sensors.
            </p>
          ) : job.sensors.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No sensors assigned to this job.
            </p>
          ) : (
            <div className="space-y-2">
              {job.sensors.map(renderAssignedSensor)}
            </div>
          )}
        </CardContent>
      </Card>

      {hasAccount ? (
        <AddAccountSensorsDialog job={job} open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} />
      ) : (
        <AddSensorsDialog job={job} open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} />
      )}

      {/* Confirm removal of sensor checklist with completed items */}
      <AlertDialog open={!!confirmRemoveTemplateId} onOpenChange={(open) => { if (!open) setConfirmRemoveTemplateId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Remove Sensor Checklist
            </AlertDialogTitle>
            <AlertDialogDescription>
              This sensor has checklist items with technician responses.
              Removing the checklist will <strong>delete all completed responses</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmRemoveTemplateId) doRemoveTemplate(confirmRemoveTemplateId);
                setConfirmRemoveTemplateId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm swap of sensor checklist with completed items */}
      <AlertDialog open={!!pendingTemplateSwap} onOpenChange={(open) => { if (!open) setPendingTemplateSwap(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Change Sensor Checklist
            </AlertDialogTitle>
            <AlertDialogDescription>
              This sensor has checklist items with technician responses.
              Changing the checklist template will <strong>replace all existing items including completed responses</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingTemplateSwap) doApplyTemplate(pendingTemplateSwap.sensorId, pendingTemplateSwap.templateId);
                setPendingTemplateSwap(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Replace
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
